import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'
import FieldHelp from '../../components/FieldHelp'
import PrintPreviewModal from '../../components/PrintPreviewModal'

interface Expense {
  id: string
  expenseNumber: string
  expenseDate: string
  postingDate: string
  vendor?: string
  description: string
  category?: string
  amount: string
  taxAmount: string
  total: string
  status: string
  currency: string
  paymentMethod?: string
  receiptUrl?: string
  account?: {
    code: string
    name: string
  }
  taxRate?: {
    name: string
    rate: string
  }
  createdBy?: string
  postedAt?: string
  postedBy?: string
}

interface Account {
  id: string
  code: string
  name: string
  rootType: string
}

interface TaxRate {
  id: string
  name: string
  rate: string
  taxType: string
}

const Expenses: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission } = usePermissions(user)
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null)
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null)
  const [isLoadingExpense, setIsLoadingExpense] = useState(false)
  const [accounts, setAccounts] = useState<Account[]>([])
  const [taxRates, setTaxRates] = useState<TaxRate[]>([])
  const [formData, setFormData] = useState({
    expenseDate: new Date().toISOString().split('T')[0],
    postingDate: new Date().toISOString().split('T')[0],
    vendor: '',
    description: '',
    category: '',
    amount: '',
    accountId: '',
    taxRateId: '',
    currency: 'GBP',
    paymentMethod: '',
    receiptUrl: '',
    receiptFile: null as File | null,
  })

  useEffect(() => {
    fetchExpenses()
    if (showCreateModal) {
      fetchAccounts()
      fetchTaxRates()
    }
  }, [showCreateModal])

  const fetchExpenses = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/expenses')
      setExpenses(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching expenses:', error)
      toast.error('Failed to load expenses')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/erp/accounts', { params: { isActive: 'true', rootType: 'EXPENSE' } })
      setAccounts(response.data.data || [])
    } catch (error) {
      console.error('Error fetching accounts:', error)
    }
  }

  const fetchTaxRates = async () => {
    try {
      const response = await axios.get('/api/erp/tax-rates', { params: { isActive: 'true' } })
      setTaxRates(response.data.data || [])
    } catch (error) {
      console.error('Error fetching tax rates:', error)
    }
  }

  const calculateTotals = () => {
    const amount = parseFloat(formData.amount) || 0
    let taxAmount = 0

    if (formData.taxRateId) {
      const taxRate = taxRates.find(tr => tr.id === formData.taxRateId)
      if (taxRate) {
        const rate = parseFloat(taxRate.rate) || 0
        taxAmount = amount * (rate / 100)
      }
    }

    return { amount, taxAmount, total: amount + taxAmount }
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingExpense) return

    if (!formData.description || !formData.amount || !formData.accountId) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      const payload = {
        expenseDate: formData.expenseDate,
        postingDate: formData.postingDate,
        vendor: formData.vendor || undefined,
        description: formData.description,
        category: formData.category || undefined,
        amount: parseFloat(formData.amount),
        accountId: formData.accountId,
        taxRateId: formData.taxRateId || undefined,
        currency: formData.currency,
        paymentMethod: formData.paymentMethod || undefined,
        receiptUrl: formData.receiptUrl || undefined,
      }

      await axios.put(`/api/erp/expenses/${editingExpense.id}`, payload)
      
      // Upload receipt if new file provided
      if (formData.receiptFile) {
        try {
          const receiptFormData = new FormData()
          receiptFormData.append('receipt', formData.receiptFile)
          await axios.post(`/api/erp/expenses/${editingExpense.id}/receipt`, receiptFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
        } catch (error: any) {
          console.error('Error uploading receipt:', error)
          toast.error('Expense updated but receipt upload failed')
        }
      }
      
      toast.success('Expense updated successfully')
      setShowEditModal(false)
      setEditingExpense(null)
      fetchExpenses()
    } catch (error: any) {
      console.error('Error updating expense:', error)
      toast.error(error.response?.data?.error || 'Failed to update expense')
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.description || !formData.amount || !formData.accountId) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      const payload = {
        expenseDate: formData.expenseDate,
        postingDate: formData.postingDate,
        vendor: formData.vendor || undefined,
        description: formData.description,
        category: formData.category || undefined,
        amount: parseFloat(formData.amount),
        accountId: formData.accountId,
        taxRateId: formData.taxRateId || undefined,
        currency: formData.currency,
        paymentMethod: formData.paymentMethod || undefined,
        receiptUrl: formData.receiptUrl || undefined,
      }

      const response = await axios.post('/api/erp/expenses', payload)
      const expenseId = response.data.data.id
      
      // Upload receipt if provided
      if (formData.receiptFile) {
        try {
          const receiptFormData = new FormData()
          receiptFormData.append('receipt', formData.receiptFile)
          await axios.post(`/api/erp/expenses/${expenseId}/receipt`, receiptFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
        } catch (error: any) {
          console.error('Error uploading receipt:', error)
          toast.error('Expense created but receipt upload failed')
        }
      }
      
      toast.success('Expense created successfully')
      setShowCreateModal(false)
      setFormData({
        expenseDate: new Date().toISOString().split('T')[0],
        postingDate: new Date().toISOString().split('T')[0],
        vendor: '',
        description: '',
        category: '',
        amount: '',
        accountId: '',
        taxRateId: '',
        currency: 'GBP',
        paymentMethod: '',
        receiptUrl: '',
        receiptFile: null,
      })
      fetchExpenses()
    } catch (error: any) {
      console.error('Error creating expense:', error)
      toast.error(error.response?.data?.error || 'Failed to create expense')
    }
  }

  const handleViewExpense = async (expenseId: string) => {
    setIsLoadingExpense(true)
    try {
      const response = await axios.get(`/api/erp/expenses/${expenseId}`)
      setSelectedExpense(response.data.data)
      setShowViewModal(true)
    } catch (error: any) {
      console.error('Error fetching expense:', error)
      toast.error('Failed to load expense details')
    } finally {
      setIsLoadingExpense(false)
    }
  }

  const handlePost = async (expenseId: string) => {
    if (!confirm('Are you sure you want to post this expense? This will create GL entries and cannot be undone.')) {
      return
    }
    try {
      await axios.post(`/api/erp/expenses/${expenseId}/post`)
      toast.success('Expense posted successfully')
      fetchExpenses()
    } catch (error: any) {
      console.error('Error posting expense:', error)
      toast.error(error.response?.data?.error || 'Failed to post expense')
    }
  }

  const handleSubmit = async (expenseId: string) => {
    try {
      await axios.post(`/api/erp/expenses/${expenseId}/submit`)
      toast.success('Expense submitted for approval')
      fetchExpenses()
    } catch (error: any) {
      console.error('Error submitting expense:', error)
      toast.error(error.response?.data?.error || 'Failed to submit expense')
    }
  }

  const handleApprove = async (expenseId: string) => {
    if (!confirm('Are you sure you want to approve this expense?')) {
      return
    }
    try {
      await axios.post(`/api/erp/expenses/${expenseId}/approve`)
      toast.success('Expense approved')
      fetchExpenses()
    } catch (error: any) {
      console.error('Error approving expense:', error)
      toast.error(error.response?.data?.error || 'Failed to approve expense')
    }
  }

  const handleReject = async (expenseId: string) => {
    if (!confirm('Are you sure you want to reject this expense?')) {
      return
    }
    try {
      await axios.post(`/api/erp/expenses/${expenseId}/reject`)
      toast.success('Expense rejected')
      fetchExpenses()
    } catch (error: any) {
      console.error('Error rejecting expense:', error)
      toast.error(error.response?.data?.error || 'Failed to reject expense')
    }
  }

  const handleDelete = async (expenseId: string) => {
    if (!confirm('Are you sure you want to delete this expense? This action cannot be undone.')) {
      return
    }
    try {
      await axios.delete(`/api/erp/expenses/${expenseId}`)
      toast.success('Expense deleted successfully')
      fetchExpenses()
    } catch (error: any) {
      console.error('Error deleting expense:', error)
      toast.error(error.response?.data?.error || 'Failed to delete expense')
    }
  }

  if (!hasPermission('erp_expense', 'read')) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You don't have permission to view expenses.</p>
        </div>
      </div>
    )
  }

  const totals = calculateTotals()

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-gray-900">Expenses</h1>
            <PageHelp pageIdentifier="expenses" title="Expenses" />
          </div>
          <p className="text-gray-600 mt-1">Expense management and accounts payable</p>
        </div>
        {hasPermission('erp_expense', 'write') && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Create Expense</span>
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : expenses.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500">No expenses found. Create your first expense to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expense #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {expenses.map((expense) => (
                <tr key={expense.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleViewExpense(expense.id)}>
                  <td className="px-4 py-2">{expense.expenseNumber}</td>
                  <td className="px-4 py-2">{new Date(expense.expenseDate).toLocaleDateString()}</td>
                  <td className="px-4 py-2">{expense.vendor || '-'}</td>
                  <td className="px-4 py-2">{expense.description}</td>
                  <td className="px-4 py-2">{expense.account ? `${expense.account.code} - ${expense.account.name}` : '-'}</td>
                  <td className="px-4 py-2 text-right">{parseFloat(expense.total).toLocaleString('en-GB', { style: 'currency', currency: expense.currency || 'GBP' })}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      expense.status === 'PAID' ? 'bg-green-100 text-green-800' :
                      expense.status === 'POSTED' ? 'bg-blue-100 text-blue-800' :
                      expense.status === 'APPROVED' ? 'bg-yellow-100 text-yellow-800' :
                      expense.status === 'SUBMITTED' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {expense.status}
                    </span>
                  </td>
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewExpense(expense.id)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        disabled={isLoadingExpense}
                      >
                        View
                      </button>
                      {expense.status === 'DRAFT' && hasPermission('erp_expense', 'write') && (
                        <>
                          <button
                            onClick={async () => {
                              setIsLoadingExpense(true)
                              try {
                                const response = await axios.get(`/api/erp/expenses/${expense.id}`)
                                const exp = response.data.data
                                setEditingExpense(exp)
                                setFormData({
                                  expenseDate: new Date(exp.expenseDate).toISOString().split('T')[0],
                                  postingDate: new Date(exp.postingDate).toISOString().split('T')[0],
                                  vendor: exp.vendor || '',
                                  description: exp.description,
                                  category: exp.category || '',
                                  amount: exp.amount.toString(),
                                  accountId: exp.accountId,
                                  taxRateId: exp.taxRateId || '',
                                  currency: exp.currency || 'GBP',
                                  paymentMethod: exp.paymentMethod || '',
                                  receiptUrl: exp.receiptUrl || '',
                                  receiptFile: null,
                                })
                                fetchAccounts()
                                fetchTaxRates()
                                setShowEditModal(true)
                              } catch (error: any) {
                                console.error('Error fetching expense:', error)
                                toast.error('Failed to load expense for editing')
                              } finally {
                                setIsLoadingExpense(false)
                              }
                            }}
                            className="text-green-600 hover:text-green-800 text-sm font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleSubmit(expense.id)}
                            className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                          >
                            Submit
                          </button>
                        </>
                      )}
                      {expense.status === 'SUBMITTED' && hasPermission('erp_expense', 'approve') && (
                        <>
                          <button
                            onClick={() => handleApprove(expense.id)}
                            className="text-green-600 hover:text-green-800 text-sm font-medium"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(expense.id)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {expense.status === 'APPROVED' && hasPermission('erp_expense', 'post') && (
                        <button
                          onClick={() => handlePost(expense.id)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Post
                        </button>
                      )}
                      {expense.status === 'DRAFT' && hasPermission('erp_expense', 'delete') && (
                        <button
                          onClick={() => handleDelete(expense.id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Expense Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create Expense</h2>
            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expense Date *
                      <FieldHelp content="The date when the expense was incurred or the receipt/bill was received. This is the business date of the expense." />
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.expenseDate}
                      onChange={(e) => setFormData({ ...formData, expenseDate: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Posting Date *
                      <FieldHelp content="The date used for accounting purposes. This determines which accounting period the expense belongs to and appears in financial reports. Usually the same as expense date, but can differ for accrual accounting or period adjustments." />
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.postingDate}
                      onChange={(e) => setFormData({ ...formData, postingDate: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                {/* Vendor and Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor/Supplier</label>
                  <input
                    type="text"
                    value={formData.vendor}
                    onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                    placeholder="Vendor name"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                  <textarea
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="What is this expense for?"
                    rows={3}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="e.g., Office Supplies, Travel, Utilities"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>

                {/* Amount and Account */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    >
                      <option value="GBP">GBP</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expense Account *</label>
                  <select
                    required
                    value={formData.accountId}
                    onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">Select expense account...</option>
                    {accounts.map(acc => (
                      <option key={acc.id} value={acc.id}>{acc.code} - {acc.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate</label>
                  <select
                    value={formData.taxRateId}
                    onChange={(e) => setFormData({ ...formData, taxRateId: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">No Tax</option>
                    {taxRates.map(tr => (
                      <option key={tr.id} value={tr.id}>{tr.name} ({tr.rate}%)</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                  <select
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">Select payment method...</option>
                    <option value="CASH">Cash</option>
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                    <option value="CHEQUE">Cheque</option>
                    <option value="CREDIT_CARD">Credit Card</option>
                    <option value="DEBIT_CARD">Debit Card</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Receipt</label>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={async (e) => {
                      const file = e.target.files?.[0]
                      if (!file) return
                      
                      // Validate file size (10MB max)
                      if (file.size > 10 * 1024 * 1024) {
                        toast.error('File size must be less than 10MB')
                        return
                      }
                      
                      // Create FormData for upload
                      const formDataToUpload = new FormData()
                      formDataToUpload.append('receipt', file)
                      
                      try {
                        // First create the expense (if not editing)
                        // For now, we'll upload after expense creation
                        // Store file for later upload
                        setFormData({ ...formData, receiptFile: file as any })
                        toast.success('Receipt selected. It will be uploaded when you create the expense.')
                      } catch (error: any) {
                        console.error('Error selecting receipt:', error)
                        toast.error('Failed to select receipt')
                      }
                    }}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                  {formData.receiptUrl && (
                    <a href={formData.receiptUrl} target="_blank" rel="noopener noreferrer" className="mt-2 text-blue-600 hover:underline text-sm">
                      View current receipt
                    </a>
                  )}
                </div>

                {/* Totals */}
                <div className="bg-gray-50 p-4 rounded border">
                  <div className="flex justify-end space-x-6">
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Amount:</p>
                      <p className="text-sm text-gray-600">Tax:</p>
                      <p className="text-lg font-bold text-gray-900">Total:</p>
                    </div>
                    <div className="text-right min-w-[120px]">
                      <p className="text-sm text-gray-900">{totals.amount.toLocaleString('en-GB', { style: 'currency', currency: formData.currency })}</p>
                      <p className="text-sm text-gray-900">{totals.taxAmount.toLocaleString('en-GB', { style: 'currency', currency: formData.currency })}</p>
                      <p className="text-lg font-bold text-gray-900">{totals.total.toLocaleString('en-GB', { style: 'currency', currency: formData.currency })}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Create Expense
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Expense Modal */}
      {showViewModal && selectedExpense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Expense {selectedExpense.expenseNumber}</h2>
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedExpense(null)
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Expense Details */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Expense Date</label>
                  <p className="mt-1 text-sm text-gray-900">{new Date(selectedExpense.expenseDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Posting Date</label>
                  <p className="mt-1 text-sm text-gray-900">{new Date(selectedExpense.postingDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <p className="mt-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      selectedExpense.status === 'PAID' ? 'bg-green-100 text-green-800' :
                      selectedExpense.status === 'POSTED' ? 'bg-blue-100 text-blue-800' :
                      selectedExpense.status === 'APPROVED' ? 'bg-yellow-100 text-yellow-800' :
                      selectedExpense.status === 'SUBMITTED' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedExpense.status}
                    </span>
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Vendor/Supplier</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedExpense.vendor || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Category</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedExpense.category || '-'}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <p className="mt-1 text-sm text-gray-900">{selectedExpense.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Expense Account</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedExpense.account ? `${selectedExpense.account.code} - ${selectedExpense.account.name}` : '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tax Rate</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedExpense.taxRate ? `${selectedExpense.taxRate.name} (${selectedExpense.taxRate.rate}%)` : 'No Tax'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Payment Method</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedExpense.paymentMethod || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Currency</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedExpense.currency || 'GBP'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Receipt</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedExpense.receiptUrl ? (
                      <a href={selectedExpense.receiptUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        View Receipt
                      </a>
                    ) : '-'}
                  </p>
                </div>
              </div>

              {/* Amounts */}
              <div className="bg-gray-50 p-4 rounded border">
                <div className="flex justify-end space-x-6">
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Amount:</p>
                    <p className="text-sm text-gray-600">Tax:</p>
                    <p className="text-lg font-bold text-gray-900">Total:</p>
                  </div>
                  <div className="text-right min-w-[120px]">
                    <p className="text-sm text-gray-900">
                      {parseFloat(selectedExpense.amount).toLocaleString('en-GB', { style: 'currency', currency: selectedExpense.currency || 'GBP' })}
                    </p>
                    <p className="text-sm text-gray-900">
                      {parseFloat(selectedExpense.taxAmount || '0').toLocaleString('en-GB', { style: 'currency', currency: selectedExpense.currency || 'GBP' })}
                    </p>
                    <p className="text-lg font-bold text-gray-900">
                      {parseFloat(selectedExpense.total).toLocaleString('en-GB', { style: 'currency', currency: selectedExpense.currency || 'GBP' })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Posted Info */}
              {selectedExpense.postedAt && (
                <div className="text-sm text-gray-500">
                  Posted on {new Date(selectedExpense.postedAt).toLocaleString()}
                  {selectedExpense.postedBy && ` by ${selectedExpense.postedBy}`}
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-between">
              <div className="flex space-x-2">
                {selectedExpense.status === 'POSTED' && hasPermission('erp_expense', 'write') && (
                  <button
                    type="button"
                    onClick={async () => {
                      const reason = prompt('Enter reason for amendment (optional):')
                      try {
                        const response = await axios.post(`/api/erp/expenses/${selectedExpense.id}/amend`, { reason: reason || undefined })
                        toast.success('Expense amended successfully')
                        setShowViewModal(false)
                        setSelectedExpense(null)
                        fetchExpenses()
                      } catch (error: any) {
                        console.error('Error amending expense:', error)
                        toast.error(error.response?.data?.error || 'Failed to amend expense')
                      }
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Amend</span>
                  </button>
                )}
                {hasPermission('erp_expense', 'write') && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const response = await axios.post(`/api/erp/expenses/${selectedExpense.id}/duplicate`)
                        toast.success('Expense duplicated successfully')
                        setShowViewModal(false)
                        setSelectedExpense(null)
                        fetchExpenses()
                      } catch (error: any) {
                        console.error('Error duplicating expense:', error)
                        toast.error(error.response?.data?.error || 'Failed to duplicate expense')
                      }
                    }}
                    className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Duplicate</span>
                  </button>
                )}
                {selectedExpense.status === 'POSTED' && hasPermission('erp_expense', 'write') && (
                  <button
                    type="button"
                    onClick={async () => {
                      const reason = prompt('Enter reason for cancellation (optional):')
                      try {
                        await axios.post(`/api/erp/expenses/${selectedExpense.id}/cancel`, { reason: reason || undefined })
                        toast.success('Expense cancelled successfully')
                        setShowViewModal(false)
                        setSelectedExpense(null)
                        fetchExpenses()
                      } catch (error: any) {
                        console.error('Error cancelling expense:', error)
                        toast.error(error.response?.data?.error || 'Failed to cancel expense')
                      }
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Cancel</span>
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedExpense(null)
                }}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Expenses
